import { AnalyticsBrowser } from "@segment/analytics-next";
import { MessageSeverity, captureError, captureMessage } from "../monitoring";
import { getSegmentConfig } from "./trackingConfig";

const analytics = new AnalyticsBrowser();
let segmentConfig = getSegmentConfig();
let isSegmentAnalyticsInitialized = false;

/**
 * Initialize segment analytics
 */
export function initialize(): void {
  initializeSegmentAnalytics().catch(() => {
    // Health issues are already reported inside initializeSegmentAnalytics.
    // This catch prevents an unhandled promise rejection.
  });
}

export function isTrackingInitialized(): boolean {
  return isSegmentAnalyticsInitialized;
}

async function initializeSegmentAnalytics(): Promise<void> {
  if (isSegmentAnalyticsInitialized || !segmentConfig.enabled) {
    return;
  }

  analytics.load({ writeKey: segmentConfig.writeKey });
  isSegmentAnalyticsInitialized = true;

  try {
    await analytics;
  } catch (error) {
    captureMessage(
      "Segment analytics failed to load",
      MessageSeverity.Warning,
      {
        error: error instanceof Error ? error.message : String(error),
        errorType: error?.constructor?.name,
      },
    );
    console.warn("[Tracking] Segment failed to load:", error);
  }
}

/**
 * Resets the tracking state (for testing purposes)
 */
export function resetTracking(): void {
  analytics.reset();
  isSegmentAnalyticsInitialized = false;
  segmentConfig = getSegmentConfig();
}

/**
 * Sets the user for tracking
 * @param user - The user to track
 */
export function setTrackingUser(user: {
  userId: number;
  email?: string;
}): void {
  if (segmentConfig.enabled) {
    analytics.identify(user.userId.toString(), {
      email: user.email,
    });
  }
}

/**
 * Clears the user being tracked.
 */
export function clearUser() {
  if (segmentConfig.enabled) {
    analytics.reset();
  }
}

/**
 * Submits an event to the tracking service.
 */
export function trackEvent(eventUid: string) {
  if (segmentConfig.enabled) {
    try {
      analytics.track("emr-event", { trackerUuid: eventUid });
    } catch (error) {
      captureError(error as Error);
    }
  }
}
